const IST_TIME_ZONE = 'Asia/Kolkata';

// One formatter for the whole app: constructing Intl.DateTimeFormat per call costs ~0.1 ms,
// and isLgBandSosDevice runs per marker per render. en-CA renders as YYYY-MM-DD.
const IST_DAY_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: IST_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
});
const DAY_KEY_CACHE = new Map();      // epoch-ms (second precision) -> key
const DAY_KEY_CACHE_MAX = 4096;

/** Calendar day key (YYYY-MM-DD) of a timestamp in IST; null if unparseable. */
export function istDayKey(value) {
  if (value == null || value === '') return null;
  const ms = value instanceof Date ? value.getTime() : (typeof value === 'number' ? value : new Date(value).getTime());
  if (Number.isNaN(ms)) return null;
  const bucket = Math.floor(ms / 1000);
  const hit = DAY_KEY_CACHE.get(bucket);
  if (hit) return hit;
  const key = IST_DAY_FORMAT.format(new Date(ms));
  if (DAY_KEY_CACHE.size >= DAY_KEY_CACHE_MAX) DAY_KEY_CACHE.clear();
  DAY_KEY_CACHE.set(bucket, key);
  return key;
}

/** True when the device's LATEST packet carries SOS status (code 11), regardless of when. */
export function hasLgBandSosStatus(device) {
  if (!device || (device.device_type !== 'lg_band' && device.device_type !== 'adafruit')) {
    return false;
  }
  const fromField = device.message_identifier != null ? String(device.message_identifier).trim() : '';
  if (fromField === '11' || fromField === '11.0') return true;
  const lt = device.latest_message_type != null ? String(device.latest_message_type).trim() : '';
  if (lt === '11' || lt.toUpperCase() === 'SOS') return true;
  if (lt.startsWith('[')) {
    try {
      const arr = JSON.parse(lt);
      if (Array.isArray(arr) && String(arr[0]).trim() === '11') return true;
    } catch (_) {
      /* ignore */
    }
  }
  return false;
}

/** When the device's latest packet (the SOS, if status is SOS) was received. */
export function getLgBandLatestPacketTime(device) {
  if (!device) return null;
  return device.latest_timestamp_iso
    || device.lastSeen
    || device.last_updated_iso
    || device.coordinates_updated_at_iso
    || null;
}

/**
 * True only when the SOS packet arrived TODAY (IST calendar day).
 * A band that went silent days ago with SOS as its last status must not keep alarming.
 * No usable timestamp -> not treated as an active SOS.
 */
export function isLgBandSosToday(device, now = Date.now()) {
  const ts = getLgBandLatestPacketTime(device);
  if (!ts) return false;
  const key = istDayKey(ts);
  return key != null && key === istDayKey(now);
}

// ---------------------------------------------------------------- SOS incident (persisted on adafruit/{docId}.sos_incident)
// An SOS is NOT cleared when the band's status moves on; it stays open until an operator closes
// it in Atlas (shared across operators via Firestore, survives reloads).

export function getSosIncident(device) {
  const inc = device?.sos_incident;
  return inc && typeof inc === 'object' ? inc : null;
}

export function hasOpenSosIncident(device) {
  return getSosIncident(device)?.status === 'open';
}

function toMs(value) {
  if (value == null || value === '') return NaN;
  const d = value instanceof Date ? value : new Date(value);
  return d.getTime();
}

/**
 * True when the band's latest packet is an SOS received today that no incident covers yet:
 * no incident at all, or the last incident was closed before this packet arrived.
 */
export function isNewSosPacket(device, now) {
  const ref = (typeof now === 'number' && Number.isFinite(now) && now > 1e12) ? now : Date.now();
  if (!hasLgBandSosStatus(device) || !isLgBandSosToday(device, ref)) return false;
  const packetMs = toMs(getLgBandLatestPacketTime(device));
  const inc = getSosIncident(device);
  if (!inc) return true;
  if (inc.status === 'open') return false;          // already tracked by an open incident
  const closedMs = toMs(inc.closed_at);
  if (Number.isFinite(closedMs)) return packetMs > closedMs;   // re-open only for a newer press
  const openedMs = toMs(inc.opened_at);
  return !Number.isFinite(openedMs) || packetMs > openedMs;
}

/** Epoch ms of the band's latest packet (NaN when unknown). */
export function getLgBandLatestPacketMs(device) {
  return toMs(getLgBandLatestPacketTime(device));
}

/**
 * True when the band's latest SOS packet is a NEWER press than the open incident records
 * (the band was pressed again while its first SOS was still open): the incident must be
 * re-stamped with the new time/position and operators re-alerted.
 */
export function isRepeatedSosPress(device, now) {
  const ref = (typeof now === 'number' && Number.isFinite(now) && now > 1e12) ? now : Date.now();
  const inc = getSosIncident(device);
  if (!inc || inc.status !== 'open') return false;
  if (!hasLgBandSosStatus(device) || !isLgBandSosToday(device, ref)) return false;
  const packetMs = getLgBandLatestPacketMs(device);
  const openedMs = toMs(inc.opened_at);
  return Number.isFinite(packetMs) && Number.isFinite(openedMs) && packetMs > openedMs;
}

/**
 * When the SOS shown for this band was raised: the open incident's opened_at, else the latest
 * packet time. A CLOSED incident's opened_at is never used (that was the previous episode).
 */
export function getSosRaisedAtIso(device) {
  const inc = getSosIncident(device);
  if (inc && inc.status === 'open' && inc.opened_at) return inc.opened_at;
  return getLgBandLatestPacketTime(device) || null;
}

/** Active SOS for banner / bell / map = an open incident, or a new SOS packet not yet recorded. */
export function isLgBandSosDevice(device, now) {
  const ref = (typeof now === 'number' && Number.isFinite(now) && now > 1e12) ? now : Date.now();
  return hasOpenSosIncident(device) || isNewSosPacket(device, ref);
}

/**
 * Where the SOS is anchored on the map: the position recorded when the incident was opened
 * (so later packets don't drag the SOS zone around), falling back to the band's live position.
 */
export function getSosAnchorLatLng(device) {
  const inc = getSosIncident(device);
  if (inc && inc.status === 'open') {
    const la = Number(inc.lat); const ln = Number(inc.lng);
    if (Number.isFinite(la) && Number.isFinite(ln) && !(la === 0 && ln === 0)) return { lat: la, lng: ln };
  }
  const la = Number(device?.last_latitude ?? device?.coordinates?.lat);
  const ln = Number(device?.last_longitude ?? device?.coordinates?.lng);
  if (Number.isFinite(la) && Number.isFinite(ln) && !(la === 0 && ln === 0)) return { lat: la, lng: ln };
  return null;
}

// ---------------------------------------------------------------- nearest tower

export function haversineMeters(aLat, aLng, bLat, bLng) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Nearest lifeguard tower to a point -> { name, distanceM } or null. towers: state.locations.towerLocations */
const TOWER_MEMO = new WeakMap();   // towers array -> Map(pointKey -> result)
/** A tower farther than this is not a usable "nearest tower" (shown as NA). */
export const NEAREST_TOWER_MAX_M = 50 * 1000;

export function findNearestTower(lat, lng, towers) {
  if (lat == null || lng == null || lat === '' || lng === '') return null;
  const la = Number(lat); const ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln) || (la === 0 && ln === 0)) return null;
  let memo = null; const key = `${la.toFixed(6)},${ln.toFixed(6)}`;
  if (towers && typeof towers === 'object') {
    memo = TOWER_MEMO.get(towers);
    if (!memo) { memo = new Map(); TOWER_MEMO.set(towers, memo); }
    if (memo.has(key)) return memo.get(key);
  }
  let best = null;
  for (const t of towers || []) {
    const tl = Number(t?.coordinates?.lat ?? t?.lat); const tn = Number(t?.coordinates?.lng ?? t?.lng);
    if (!Number.isFinite(tl) || !Number.isFinite(tn) || (tl === 0 && tn === 0)) continue;
    const d = haversineMeters(la, ln, tl, tn);
    if (!best || d < best.distanceM) best = { name: t.name || t.id || 'Tower', distanceM: Math.round(d) };
  }
  if (best && best.distanceM > NEAREST_TOWER_MAX_M) best = null;   // > 50 km: no meaningful nearest tower
  if (memo) { if (memo.size >= GEO_MEMO_MAX) memo.clear(); memo.set(key, best); }
  return best;
}

export function formatDistance(m) {
  if (m == null || !Number.isFinite(Number(m))) return null;
  const v = Number(m);
  return v < 1000 ? `${Math.round(v)} m` : `${(v / 1000).toFixed(v < 10000 ? 2 : 1)} km`;
}

// ---------------------------------------------------------------- geofence check for an SOS

// Out-of-geofence SOS message, three parts: title, nearest beach (computed), police numbers.
export const SOS_OUTSIDE_GEOFENCE_TITLE = 'SOS: Incident alert, outside Geofenced Zone';
export const SOS_POLICE_HELP_MESSAGE = 'Please call the police for help. Here are the numbers:';
/** Emergency police numbers shown with an out-of-geofence SOS (India). Extend per deployment. */
export const SOS_POLICE_NUMBERS = [
  { number: '112', label: 'Emergency (all services)' },
  { number: '100', label: 'Police' },
];

const NEAREST_GEOFENCE_MEMO = new WeakMap();   // geofences array -> Map(pointKey -> result)

/**
 * Nearest beach geofence to a point: shortest haversine distance to any polygon vertex or the
 * polygon centroid (0 m when the point is inside). Used for "Nearest Beach" on out-of-zone SOS.
 */
export function findNearestGeofence(lat, lng, geofences) {
  const la = Number(lat); const ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln) || (la === 0 && ln === 0)) return null;
  let memo = null; const key = `${la.toFixed(6)},${ln.toFixed(6)}`;
  if (geofences && typeof geofences === 'object') {
    memo = NEAREST_GEOFENCE_MEMO.get(geofences);
    if (!memo) { memo = new Map(); NEAREST_GEOFENCE_MEMO.set(geofences, memo); }
    if (memo.has(key)) return memo.get(key);
  }
  let best = null;
  for (const g of geofences || []) {
    const poly = normalizeGeofencePolygon(g);
    if (!poly.length) continue;
    const name = g.name || g.id || 'Beach';
    if (pointInLatLngPolygon(la, ln, poly)) { best = { name, distanceM: 0 }; break; }
    let cLat = 0; let cLng = 0; let d = Infinity;
    for (const [pLat, pLng] of poly) {
      cLat += pLat; cLng += pLng;
      d = Math.min(d, haversineMeters(la, ln, pLat, pLng));
    }
    d = Math.min(d, haversineMeters(la, ln, cLat / poly.length, cLng / poly.length));
    if (!best || d < best.distanceM) best = { name, distanceM: Math.round(d) };
  }
  if (memo) { if (memo.size >= GEO_MEMO_MAX) memo.clear(); memo.set(key, best); }
  return best;
}

export const SOS_OUTSIDE_GEOFENCE_MESSAGE =
  'The SOS was detected out of the Geofenced zone, Please Call the Police for Help';

/** Geofence polygon in any of the shapes Atlas stores -> [[lat, lng], ...] */
export function normalizeGeofencePolygon(geofence) {
  const raw = Array.isArray(geofence?.polygon) ? geofence.polygon
    : Array.isArray(geofence?.coordinates) ? geofence.coordinates
      : Array.isArray(geofence) ? geofence : [];
  const out = [];
  for (const p of raw) {
    if (p == null) continue;
    let lat; let lng;
    if (typeof p.lat === 'number' && typeof p.lng === 'number') { lat = p.lat; lng = p.lng; }
    else if (typeof p.latitude === 'number' && typeof p.longitude === 'number') { lat = p.latitude; lng = p.longitude; }
    else if (Array.isArray(p) && p.length >= 2) { lat = Number(p[0]); lng = Number(p[1]); }
    if (Number.isFinite(lat) && Number.isFinite(lng)) out.push([lat, lng]);
  }
  return out.length >= 3 ? out : [];
}

function pointInLatLngPolygon(lat, lng, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [latI, lngI] = polygon[i];
    const [latJ, lngJ] = polygon[j];
    const intersect = ((latI > lat) !== (latJ > lat))
      && (lng < (lngJ - lngI) * (lat - latI) / (latJ - latI) + lngI);
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * -> { hasPosition, inside, geofenceName }.
 * hasPosition false = band sent no usable coordinates: we cannot say it is outside.
 */
const GEO_STATUS_MEMO = new WeakMap();   // geofences array -> Map(pointKey -> status)
const GEO_MEMO_MAX = 2048;

export function getSosGeofenceStatus(device, geofences) {
  const lat = device?.last_latitude ?? device?.coordinates?.lat;
  const lng = device?.last_longitude ?? device?.coordinates?.lng;
  const la = Number(lat); const ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln) || (la === 0 && ln === 0)) {
    return { hasPosition: false, inside: false, geofenceName: null };
  }
  // Memo: the SOS anchor is fixed for an open incident and this runs on every Firestore snapshot.
  let memo = null;
  if (geofences && typeof geofences === 'object') {
    memo = GEO_STATUS_MEMO.get(geofences);
    if (!memo) { memo = new Map(); GEO_STATUS_MEMO.set(geofences, memo); }
    const hit = memo.get(`${la.toFixed(6)},${ln.toFixed(6)}`);
    if (hit) return hit;
  }
  const result = computeGeofenceStatus(la, ln, geofences);
  if (memo) {
    if (memo.size >= GEO_MEMO_MAX) memo.clear();
    memo.set(`${la.toFixed(6)},${ln.toFixed(6)}`, result);
  }
  return result;
}

function computeGeofenceStatus(la, ln, geofences) {
  for (const g of geofences || []) {
    const poly = normalizeGeofencePolygon(g);
    if (poly.length && pointInLatLngPolygon(la, ln, poly)) {
      return { hasPosition: true, inside: true, geofenceName: g.name || g.id || null };
    }
  }
  return { hasPosition: true, inside: false, geofenceName: null };
}

/** True only when the band HAS a position and it is outside every known geofence. */
export function isSosOutsideGeofences(device, geofences) {
  const st = getSosGeofenceStatus(device, geofences);
  return st.hasPosition && !st.inside;
}

/** Display ID for SOS banner and alerts. */
export function getLgBandDisplayId(device) {
  if (!device) return 'Unknown';
  return (
    device.deviceId
    || device.adafruit_hardware_device_id
    || device.adafruit_firestore_doc_id
    || device.displayName
    || device.name
    || device.device_id
    || 'Unknown'
  );
}

export function buildSosAlertMessage(device) {
  return `SOS Initiated: LG Band: ${getLgBandDisplayId(device)}`;
}

/** LG Band row from state.locations.adafruitLocations -> map/device shape used by selection + navigation. */
export function toLgBandMapDevice(row) {
  if (!row) return null;
  const lat = row.coordinates && typeof row.coordinates.lat === 'number' ? row.coordinates.lat : row.last_latitude;
  const lng = row.coordinates && typeof row.coordinates.lng === 'number' ? row.coordinates.lng : row.last_longitude;
  return {
    ...row,
    device_type: 'lg_band',
    last_latitude: lat,
    last_longitude: lng,
    displayName: row.displayName || row.name || row.deviceId,
  };
}

/**
 * LG Bands currently in SOS. Source of truth is state.locations.adafruitLocations —
 * NOT state.devices.allDevices, which only ever holds fleet vehicles.
 */
export function findSosLgBandDevices(adafruitLocations) {
  // NOTE: never pass isLgBandSosDevice straight to .filter — the index would land in its `now` param.
  return (adafruitLocations || []).filter((row) => isLgBandSosDevice(row)).map(toLgBandMapDevice);
}

/** One notification per SOS episode (band stays in code 11 until its next routine packet). */
export function sosNotificationIdFor(device) {
  const ts = (device?.latest_timestamp_iso || '').slice(0, 16).replace(/[^0-9T]/g, '');
  return `sos_${device?.device_id || 'unknown'}_${ts || 'now'}`;
}
